import { FC, useCallback, useMemo, useState } from 'react'

import { Box, Divider } from '@mui/material'

import { MOTIVATION_SOURCE_ORDER } from '../../helpers/motivationSourceColors'
import { MotivationScoresBySource, MotivationSource } from '../../types'
import { MotivationBarChart } from '../MotivationBarChart/MotivationBarChart'
import { MotivationRadarChart } from '../MotivationRadarChart/MotivationRadarChart'
import { MotivationSourceLegend } from '../MotivationSourceLegend/MotivationSourceLegend'

interface MotivationsPanelProps {
  motivationScores: MotivationScoresBySource
  /**
   * Called with the raw motivation label when the user clicks a bar or row
   * label in the right-hand bar chart. Threaded straight through to
   * {@link MotivationBarChart}; when omitted the chart is non-interactive.
   */
  onSelectMotivation?: (motivationId: string) => void
}

/**
 * Renders the radar + bar chart pair under a shared legend. Owns the
 * `hiddenSources` toggle state so clicking the shared legend updates both
 * charts simultaneously, restoring the click-to-hide behaviour the per-chart
 * Chart.js legends used to provide.
 *
 * The legend only lists sources whose data is actually available on the game,
 * so games without an in-game (Game & Live Events) profile don't surface a
 * toggle for a series that doesn't render.
 */
export const MotivationsPanel: FC<MotivationsPanelProps> = ({ motivationScores, onSelectMotivation }) => {
  const [hiddenSources, setHiddenSources] = useState<ReadonlySet<MotivationSource>>(() => new Set())

  const availableSources = useMemo<readonly MotivationSource[]>(
    () => MOTIVATION_SOURCE_ORDER.filter((source) => motivationScores[source] !== null),
    [motivationScores]
  )

  const toggleSource = useCallback((source: MotivationSource) => {
    setHiddenSources((prev) => {
      const next = new Set(prev)
      if (next.has(source)) {
        next.delete(source)
      } else {
        next.add(source)
      }
      return next
    })
  }, [])

  return (
    <>
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
        {/*
         * Radar gets a slightly larger horizontal share than the bar chart so
         * the polygon has room to breathe; the bar chart keeps the same flex
         * weight as before but inherits the taller container which spaces the
         * motivation rows further apart for a cleaner read.
         */}
        <Box sx={{ flex: { xs: 1, md: 1.15 }, minWidth: 0 }}>
          <MotivationRadarChart motivationScores={motivationScores} hiddenSources={hiddenSources} />
        </Box>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Divider orientation="horizontal" flexItem sx={{ display: { xs: 'block', md: 'none' } }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MotivationBarChart motivationScores={motivationScores} hiddenSources={hiddenSources} onSelectMotivation={onSelectMotivation} />
        </Box>
      </Box>
      <Divider sx={{ mt: 2 }} />
      <MotivationSourceLegend availableSources={availableSources} hiddenSources={hiddenSources} onToggle={toggleSource} />
    </>
  )
}
